/**
 * The browser side of an online season: sign-in, the lobby, and one quarter at a time.
 * During a quarter the game runs only your own moves on your view (instant feedback); each move that works is
 * recorded and saved to the server as a draft. Ready sends them for real; when all four are ready (or the timer
 * runs out) the server settles the quarter and everyone loads their new view.
 */
import { createClient, type RealtimeChannel, type Session, type SupabaseClient } from "@supabase/supabase-js";
import type { Command, LogEntry } from "@bd/sim";
import { host, me, onMove, setMyPlayer, useGame, type OnlineState, type Toast } from "../store";
import { callServer, gunzipBase64, gzipBase64, myResults, myView, readyList, seasonMembers, getSeason, submitQuarter } from "./api";

const URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined;
export const sb: SupabaseClient | null = URL && KEY ? createClient(URL, KEY) : null;

export async function session(): Promise<Session | null> {
  return sb ? (await sb.auth.getSession()).data.session : null;
}

/** Whether Discord sign-in is switched on for the project yet (Supabase publishes this). */
export async function discordReady(): Promise<boolean> {
  if (!URL || !KEY) return false;
  try {
    const r = await fetch(`${URL}/auth/v1/settings`, { headers: { apikey: KEY } });
    return !!(await r.json())?.external?.discord;
  } catch {
    return false;
  }
}

export async function signInWithDiscord(): Promise<void> {
  await sb?.auth.signInWithOAuth({ provider: "discord", options: { redirectTo: `${location.origin}${location.pathname}?online=1` } });
}

/** Test accounts only (bots and QA): the lobby shows this form with ?qa in the address. */
export async function signInWithPassword(email: string, password: string): Promise<string | null> {
  const r = await sb!.auth.signInWithPassword({ email, password });
  return r.error ? r.error.message : null;
}

export async function signOut(): Promise<void> {
  await sb?.auth.signOut();
}

// ------------------------------------------------------------------ in a season
let channel: RealtimeChannel | null = null;
let poll: ReturnType<typeof setInterval> | null = null;
let draft: ReturnType<typeof setTimeout> | null = null;
let names: Record<number, { company: string; name: string }> = {};

const patch = (p: Partial<OnlineState>) => {
  const o = useGame.getState().online;
  if (o) useGame.setState({ online: { ...o, ...p } });
};

/** Joins a playing season: loads your view, puts back any moves you'd already made this quarter, starts listening. */
export async function enterSeason(seasonId: string): Promise<void> {
  if (!sb) return;
  const uid = (await sb.auth.getUser()).data.user!.id;
  const [season, members, view] = await Promise.all([getSeason(sb, seasonId), seasonMembers(sb, seasonId), myView(sb, seasonId)]);
  const mine = members.find((m) => m.user_id === uid);
  if (!mine || !view) throw new Error("You're not in this season.");
  const { data: players } = await sb.from("players").select("user_id, display_name");
  const display = new Map((players ?? []).map((p: { user_id: string; display_name: string }) => [p.user_id, p.display_name]));
  names = Object.fromEntries(members.map((m) => [m.seat, { company: m.company_name, name: display.get(m.user_id) ?? `Seat ${m.seat + 1}` }]));
  setMyPlayer(mine.player_id);
  host.load(view.state, { player: mine.player_id, online: true });
  useGame.setState({
    lobby: false, onboarding: 4, screen: null, selectedRoom: null, closeup: null, speed: 0,
    online: {
      seasonId, name: season.name, seat: mine.seat, playerId: mine.player_id, quarter: view.quarter, week: view.week, deadline: season.deadline,
      status: season.status, ready: false, moves: [], seats: [], settling: false, report: null,
    },
  });
  // moves already made this quarter (another device, or before a reload) are replayed onto the view
  const { data: row } = await sb.from("quarter_commands").select("commands, ready").eq("season_id", seasonId).eq("quarter", view.quarter).maybeSingle();
  const saved = (row?.commands ?? []) as Command[];
  const kept: Command[] = [];
  for (const c of saved) if ((await host.command(c)).ok) kept.push(c);
  patch({ moves: kept, ready: !!row?.ready && kept.length === saved.length });
  onMove(recordMove);
  listen(seasonId, mine.seat);
  await refresh();
}

export function leaveSeason(): void {
  onMove(null);
  setMyPlayer(null);
  if (channel) void sb?.removeChannel(channel);
  channel = null;
  if (poll) clearInterval(poll);
  poll = null;
}

function recordMove(c: Command): void {
  const o = useGame.getState().online;
  if (!o) return;
  // changing anything after pressing Ready takes the Ready back, so nobody settles on moves you didn't mean
  if (o.ready) useGame.getState().toast({ title: "Ready taken back", text: "You changed something. Press Ready again when you're done.", tone: "info" });
  patch({ moves: [...o.moves, c], ready: false });
  if (draft) clearTimeout(draft);
  draft = setTimeout(() => void saveDraft(), 1500);
}

async function saveDraft(): Promise<void> {
  const o = useGame.getState().online;
  if (!o || !sb) return;
  try {
    await submitQuarter(sb, o.seasonId, o.quarter, o.moves, o.ready);
  } catch {
    // the quarter may have just settled; refresh() picks that up
  }
}

/** Press (or take back) Ready. When you're the last one, this also asks the server to settle the quarter. */
export async function setReady(ready: boolean): Promise<void> {
  const o = useGame.getState().online;
  if (!o || !sb) return;
  if (draft) clearTimeout(draft);
  try {
    await submitQuarter(sb, o.seasonId, o.quarter, o.moves, ready);
    patch({ ready });
  } catch (e) {
    useGame.getState().toast({ title: "Not sent", text: e instanceof Error ? e.message : "Try again in a moment.", tone: "warn" });
    return;
  }
  await refresh();
}

/** Checks who is ready and whether the quarter has settled; settles it when everyone is ready or time is up. */
export async function refresh(): Promise<void> {
  const o = useGame.getState().online;
  if (!o || !sb) return;
  const season = await getSeason(sb, o.seasonId);
  if (season.quarter > o.quarter) return await loadNewQuarter(o.quarter);
  const list = await readyList(sb, o.seasonId);
  const here = new Set(Object.keys(channel?.presenceState() ?? {}).map(Number));
  patch({
    status: season.status, deadline: season.deadline,
    seats: list.map((r) => ({ seat: r.seat, ready: r.ready, here: here.has(r.seat) || r.seat === o.seat, company: names[r.seat]?.company ?? "", name: names[r.seat]?.name ?? "" })),
  });
  const everyone = list.length > 0 && list.every((r) => r.ready);
  const late = !!season.deadline && new Date(season.deadline).getTime() < Date.now();
  if (season.status === "playing" && (everyone || late) && !useGame.getState().online?.settling) {
    patch({ settling: true });
    const r = await callServer(sb, "resolve", o.seasonId);
    patch({ settling: false });
    if (r.status === "resolved") await loadNewQuarter(o.quarter);
  }
}

/** The quarter settled: load the new view and show what happened. */
async function loadNewQuarter(settled: number): Promise<void> {
  const o = useGame.getState().online;
  if (!o || !sb) return;
  const [view, results, season] = await Promise.all([myView(sb, o.seasonId), myResults(sb, o.seasonId, settled), getSeason(sb, o.seasonId)]);
  if (!view) return;
  const co = view.state.companies.find((c) => c.player === o.playerId);
  const lines: { text: string; tone: Toast["tone"] }[] = [];
  (results ?? []).forEach((r, i) => {
    if (!r.ok) lines.push({ text: `One of your moves didn't go through (${describe(o.moves[i])}): ${r.error}`, tone: "warn" });
  });
  // only what is new since the quarter began (the old view already had the rest)
  const seen = new Set((useGame.getState().game?.log ?? []).map((e: LogEntry) => `${e.week}|${e.text}`));
  const news = view.state.log.filter((e: LogEntry) => e.week >= o.week && (!e.company || e.company === co?.id) && !seen.has(`${e.week}|${e.text}`));
  for (const e of news.slice(-30)) lines.push({ text: e.text, tone: TONE[e.kind] ?? "info" });
  host.load(view.state, { player: o.playerId, online: true });
  patch({ quarter: view.quarter, week: view.week, deadline: season.deadline, status: season.status, ready: false, moves: [], settling: false, report: { quarter: settled, lines }, seats: o.seats.map((x) => ({ ...x, ready: false })) });
  useGame.setState({ screen: { kind: "quarter" } });
  void refresh();
}

const TONE: Record<string, Toast["tone"]> = { release: "good", soldout: "good", combo: "good", achievement: "good", award: "good", misprint: "bad", quit: "bad", bankrupt: "bad", overdraft: "bad", returns: "warn", poach: "warn" };

function describe(c: Command | undefined): string {
  if (!c) return "a move";
  return c.type.replace(/([A-Z])/g, " $1").toLowerCase();
}

function listen(seasonId: string, seat: number): void {
  if (!sb) return;
  if (channel) void sb.removeChannel(channel);
  channel = sb
    .channel(`season:${seasonId}`, { config: { presence: { key: String(seat) } } })
    .on("postgres_changes", { event: "UPDATE", schema: "public", table: "seasons", filter: `id=eq.${seasonId}` }, () => void refresh())
    .on("presence", { event: "sync" }, () => void refresh())
    .subscribe((status) => {
      if (status === "SUBSCRIBED") void channel?.track({ seat, at: Date.now() });
    });
  if (poll) clearInterval(poll);
  // a slow, steady check too: Ready lights, the timer, and in case a live update was missed
  poll = setInterval(() => void refresh(), 20_000);
}

// ------------------------------------------------------------------ cloud saves and feedback
export async function cloudSave(slot: string, label: string, week: number, state: unknown): Promise<string | null> {
  if (!sb) return "Online play isn't set up in this build.";
  const user = (await sb.auth.getUser()).data.user;
  if (!user) return "Sign in first (Play with the cousins).";
  const { error } = await sb.from("cloud_saves").upsert({ user_id: user.id, slot, label, week, data: await gzipBase64(JSON.stringify(state)), updated_at: new Date().toISOString() });
  return error ? error.message : null;
}

export async function cloudList(): Promise<{ slot: string; label: string; week: number; updated_at: string }[]> {
  if (!sb || !(await session())) return [];
  const { data } = await sb.from("cloud_saves").select("slot, label, week, updated_at").order("updated_at", { ascending: false });
  return data ?? [];
}

export async function cloudLoad(slot: string): Promise<unknown | null> {
  if (!sb) return null;
  const { data } = await sb.from("cloud_saves").select("data").eq("slot", slot).maybeSingle();
  if (!data) return null;
  return JSON.parse(await gunzipBase64(data.data));
}

/** Sends a feedback report with the exact game state attached, so the problem can be replayed. */
export async function sendFeedback(text: string, screen: string): Promise<string | null> {
  if (!sb) return "Online play isn't set up in this build.";
  const user = (await sb.auth.getUser()).data.user;
  if (!user) return "Sign in first (Play with the cousins) so we know who sent it.";
  const s = useGame.getState();
  const o = s.online;
  const { error } = await sb.from("feedback").insert({
    user_id: user.id, text: text.trim(), screen, season_id: o?.seasonId ?? null, quarter: o?.quarter ?? null, week: s.game?.week ?? null,
    state: s.game ? await gzipBase64(JSON.stringify({ game: s.game, moves: o?.moves ?? [], company: me(s.game)?.id })) : null,
  });
  return error ? error.message : null;
}
